<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import UiNotImplemented from '@/components/ui/UiNotImplemented.vue'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import type { Permission } from '@/types/api'

/**
 * The nav shows the whole product shape, including what is not built yet. Roadmap items
 * are rendered through `UiNotImplemented`: visible, honestly labelled, and unable to
 * navigate anywhere. An operator should be able to see where the product is going
 * without ever being fooled into thinking a slice already landed.
 */
interface BuiltItem {
  kind: 'built'
  label: string
  icon: string
  to: { name: string }
  permission?: Permission
  /** Hidden, not merely disabled, without the permission: the operator's own console. */
  hiddenWithout?: boolean
}

interface RoadmapItem {
  kind: 'roadmap'
  label: string
  icon: string
  slice: string
  detail: string
}

interface NavGroup {
  heading: string
  items: Array<BuiltItem | RoadmapItem>
}

const ui = useUiStore()
const auth = useAuthStore()
const { sidebarCollapsed, sidebarOpenMobile, theme } = storeToRefs(ui)
const { t } = useI18n()

const GROUPS: NavGroup[] = [
  {
    heading: 'nav.groups.operations',
    items: [
      { kind: 'built', label: 'nav.items.dashboard', icon: '▤', to: { name: 'dashboard' } },
      { kind: 'built', label: 'nav.items.liveoperations', icon: '▶', to: { name: 'live' } },
      { kind: 'built', label: 'nav.items.operationsreport', icon: '◷', to: { name: 'operations' } },
      { kind: 'built', label: 'nav.items.counterresponse', icon: '⇄', to: { name: 'service-report' } },
      { kind: 'built', label: 'nav.items.alerts', icon: '◈', to: { name: 'alerts' }, permission: 'events.read' },
    ],
  },
  {
    heading: 'nav.groups.evidence',
    items: [
      { kind: 'built', label: 'nav.items.eventtimeline', icon: '≡', to: { name: 'events' } },
      {
        kind: 'built',
        label: 'nav.items.reviewqueue',
        icon: '✓',
        to: { name: 'review-queue' },
        permission: 'events.review',
      },
      { kind: 'built', label: 'nav.items.quality', icon: '★', to: { name: 'quality' }, permission: 'dashboard.read' },
    ],
  },
  {
    heading: 'nav.groups.configuration',
    items: [
      { kind: 'built', label: 'nav.items.sites', icon: '⌂', to: { name: 'sites' }, permission: 'sites.read' },
      { kind: 'built', label: 'nav.items.cameras', icon: '◉', to: { name: 'cameras' } },
      { kind: 'built', label: 'nav.items.zones', icon: '⬠', to: { name: 'zones' } },
      {
        kind: 'built',
        label: 'nav.items.usecaselibrary',
        icon: '❖',
        to: { name: 'use-case-library' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.integrations',
        icon: '⇆',
        to: { name: 'integrations' },
        permission: 'settings.manage',
      },
    ],
  },
  {
    heading: 'nav.groups.peopleandprocess',
    items: [
      {
        kind: 'built',
        label: 'nav.items.employees',
        icon: '☰',
        to: { name: 'employees' },
        permission: 'settings.manage',
      },
      {
        kind: 'built',
        label: 'nav.items.customerjourneys',
        icon: '↝',
        to: { name: 'journeys' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.aiwatches',
        icon: '◎',
        to: { name: 'watches' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.sops',
        icon: '✓',
        to: { name: 'sops' },
        permission: 'dashboard.read',
      },
    ],
  },
  {
    heading: 'nav.groups.intelligence',
    items: [
      {
        kind: 'built',
        label: 'nav.items.kpis',
        icon: '▣',
        to: { name: 'kpis' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.behaviourreport',
        icon: '▥',
        to: { name: 'behaviour-report' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.aimanager',
        icon: '✧',
        to: { name: 'ai-manager' },
        permission: 'dashboard.read',
      },
      {
        kind: 'built',
        label: 'nav.items.reports',
        icon: '▦',
        to: { name: 'reports' },
        permission: 'dashboard.read',
      },
    ],
  },
  {
    heading: 'nav.groups.settings',
    items: [
      { kind: 'built', label: 'nav.items.edgenodes', icon: '⬢', to: { name: 'edge-nodes' } },
      {
        kind: 'built',
        label: 'nav.items.aiproviders',
        icon: '✦',
        to: { name: 'ai-providers' },
        permission: 'settings.manage',
      },
      { kind: 'built', label: 'nav.items.users', icon: '☺', to: { name: 'users' }, permission: 'users.manage' },
      {
        kind: 'built',
        label: 'nav.items.customers',
        icon: '⌘',
        to: { name: 'customers' },
        permission: 'platform.manage',
        hiddenWithout: true,
      },
    ],
  },
]

/** Items the account cannot read are shown disabled rather than hidden. */
function isPermitted(item: BuiltItem): boolean {
  return item.permission === undefined || auth.hasPermission(item.permission)
}

const logoSrc = computed(() =>
  theme.value === 'light' ? '/brand/u-observe-lockup.png' : '/brand/u-observe-lockup-dark.png',
)

const markSrc = computed(() =>
  theme.value === 'light' ? '/brand/u-observe-mark.png' : '/brand/u-observe-mark-dark.png',
)
</script>

<template>
  <aside
    :class="['sidebar', { 'is-collapsed': sidebarCollapsed, 'is-open-mobile': sidebarOpenMobile }]"
    aria-label="Primary navigation"
  >
    <div class="sidebar__brand">
      <RouterLink :to="{ name: 'dashboard' }" class="sidebar__brand-link" :aria-label="t('nav.brandLink')">
        <img
          v-if="!sidebarCollapsed"
          :src="logoSrc"
          class="sidebar__lockup"
          alt="U-Observe AI"
          width="1136"
          height="273"
        />
        <img v-else :src="markSrc" class="sidebar__mark" alt="U-Observe AI" width="401" height="269" />
      </RouterLink>
    </div>

    <nav class="sidebar__nav">
      <div v-for="group in GROUPS" :key="group.heading" class="sidebar__group">
        <h2 v-if="!sidebarCollapsed" class="sidebar__heading">{{ t(group.heading) }}</h2>
        <ul class="sidebar__items">
          <li
            v-for="item in group.items.filter((i) => i.kind !== 'built' || !i.hiddenWithout || isPermitted(i))"
            :key="item.label"
          >
            <RouterLink
              v-if="item.kind === 'built' && isPermitted(item)"
              :to="item.to"
              class="sidebar__link"
              :title="sidebarCollapsed ? t(item.label) : undefined"
            >
              <span class="sidebar__icon" aria-hidden="true">{{ item.icon }}</span>
              <span v-if="!sidebarCollapsed" class="sidebar__label">{{ t(item.label) }}</span>
              <span v-else class="sr-only">{{ t(item.label) }}</span>
            </RouterLink>

            <span
              v-else-if="item.kind === 'built'"
              class="sidebar__link is-forbidden"
              :title="t('nav.requiresPermission', { item: t(item.label), permission: item.permission })"
              aria-disabled="true"
            >
              <span class="sidebar__icon" aria-hidden="true">{{ item.icon }}</span>
              <span v-if="!sidebarCollapsed" class="sidebar__label">{{ t(item.label) }}</span>
              <span class="sr-only">{{ t('nav.requiresPermission', { item: t(item.label), permission: item.permission }) }}</span>
            </span>

            <UiNotImplemented
              v-else
              variant="nav"
              :feature="t(item.label)"
              :slice="item.slice"
              :detail="item.detail"
              :icon="item.icon"
            >
              <template v-if="!sidebarCollapsed">{{ t(item.label) }}</template>
            </UiNotImplemented>
          </li>
        </ul>
      </div>
    </nav>

    <button
      type="button"
      class="sidebar__collapse"
      :aria-label="t(sidebarCollapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar')"
      :aria-expanded="!sidebarCollapsed"
      @click="ui.toggleSidebar()"
    >
      <span aria-hidden="true">{{ sidebarCollapsed ? '»' : '«' }}</span>
      <span v-if="!sidebarCollapsed" class="sidebar__collapse-label">{{ t('common.actions.collapse') }}</span>
    </button>
  </aside>
</template>

<style scoped>
.sidebar {
  display: flex;
  flex-direction: column;
  width: var(--sidebar-width);
  flex-shrink: 0;
  background: var(--surface-1);
  border-inline-end: 1px solid var(--border-subtle);
  height: 100vh;
  position: sticky;
  top: 0;
  transition: width var(--transition);
}

.sidebar.is-collapsed {
  width: var(--sidebar-width-collapsed);
}

.sidebar__brand {
  display: flex;
  align-items: center;
  height: var(--topbar-height);
  padding: 0 var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}

.sidebar__brand-link {
  display: flex;
  align-items: center;
  min-width: 0;
  width: 100%;
}

.sidebar__brand-link:hover {
  text-decoration: none;
}

.sidebar__lockup {
  height: 26px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.sidebar__mark {
  height: 26px;
  width: auto;
  margin: 0 auto;
  object-fit: contain;
}

.sidebar__nav {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-3) var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.sidebar__heading {
  padding: 0 var(--space-3);
  margin-bottom: var(--space-2);
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--text-muted);
}

.sidebar__items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  color: var(--text-secondary);
  font-size: var(--text-sm);
  border: 1px solid transparent;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.sidebar__link:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
  text-decoration: none;
}

.sidebar__link.router-link-active {
  background: var(--accent-soft);
  border-color: var(--accent-border);
  color: var(--accent);
  font-weight: 600;
}

.sidebar__link.is-forbidden {
  opacity: 0.5;
  cursor: not-allowed;
}

.sidebar__icon {
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  font-size: var(--text-base);
}

.sidebar__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar__collapse {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border: none;
  border-top: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-muted);
  font-size: var(--text-xs);
  cursor: pointer;
  flex-shrink: 0;
}

.sidebar__collapse:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

.is-collapsed .sidebar__collapse {
  justify-content: center;
}

@media (max-width: 1024px) {
  .sidebar {
    position: fixed;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform var(--transition);
    box-shadow: var(--shadow-modal);
  }

  :global([dir='rtl']) .sidebar {
    transform: translateX(100%);
  }

  .sidebar.is-open-mobile {
    transform: translateX(0);
  }
}
</style>
